import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Users, Mail, Briefcase, Settings, LogOut, Crown, Building2, UserCog, Mic, Inbox, BarChart3, Eye, BookOpen, LifeBuoy, Menu, X as XIcon,
} from "lucide-react";

const ADMIN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/guests", label: "Guests", icon: Users, testid: "nav-guests" },
  { to: "/email-templates", label: "Email Templates", icon: Mail, testid: "nav-emails" },
  { to: "/sponsors", label: "Sponsors", icon: Briefcase, testid: "nav-sponsors" },
  { to: "/show-settings", label: "Show Settings", icon: Settings, testid: "nav-settings" },
  { to: "/help", label: "Getting Started", icon: BookOpen, testid: "nav-help" },
];

const SUPER_ADMIN_NAV = [
  { to: "/admin/platform", label: "Platform", icon: BarChart3, testid: "nav-platform" },
  { to: "/admin/shows", label: "All Shows", icon: Building2, testid: "nav-all-shows" },
  { to: "/admin/users", label: "User Accounts", icon: UserCog, testid: "nav-users" },
  { to: "/admin/access-requests", label: "Access Requests", icon: Inbox, testid: "nav-access-requests" },
];

const resolveAsset = (u) => (u?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function SidebarBrand({ isSuperAdmin, show }) {
  const logoSrc = show?.podcast_logo_url ? resolveAsset(show.podcast_logo_url) : null;
  const subtitle = isSuperAdmin ? "Platform Owner" : (show?.show_name || "Set up your show");
  return (
    <div style={{ padding: "0 24px 18px", borderBottom: "1.5px solid rgba(245,228,201,0.18)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {isSuperAdmin ? (
          <div style={{ width: 44, height: 44, background: "var(--pgm-amber)", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid var(--pgm-amber)" }}>
            <Crown size={20} color="var(--pgm-wine)" />
          </div>
        ) : logoSrc ? (
          <img src={logoSrc} alt="logo" style={{ width: 44, height: 44, objectFit: "cover", border: "1.5px solid var(--pgm-amber)" }} data-testid="sidebar-logo" />
        ) : (
          <div style={{ width: 44, height: 44, background: "var(--pgm-magenta)", display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid var(--pgm-amber)" }}>
            <Mic size={20} color="var(--pgm-cream)" />
          </div>
        )}
        <div>
          <div className="pgm-display" style={{ color: "var(--pgm-cream)", fontSize: 19, lineHeight: 1.05 }}>
            ShowRunner<br/>HQ
          </div>
          <div style={{ fontSize: 11, letterSpacing: "0.16em", color: "var(--pgm-amber)", textTransform: "uppercase", marginTop: 2 }} data-testid="sidebar-subtitle">{subtitle}</div>
        </div>
      </div>
    </div>
  );
}

function SidebarNav({ isSuperAdmin }) {
  const items = isSuperAdmin ? SUPER_ADMIN_NAV : ADMIN_NAV;
  return (
    <>
      <div className="pgm-sidebar-section">{isSuperAdmin ? "Platform" : "My Show"}</div>
      {items.map(({ to, label, icon: Icon, testid }) => (
        <NavLink key={to} to={to} data-testid={testid} className={({ isActive }) => isActive ? "active" : ""}>
          <Icon size={16} /> {label}
        </NavLink>
      ))}
    </>
  );
}

function SidebarUser({ user, show, isSuperAdmin, onLogout, supportEmail }) {
  return (
    <div style={{ padding: "20px 24px 8px", marginTop: 20, borderTop: "1.5px solid rgba(245,228,201,0.18)" }}>
      {supportEmail && !isSuperAdmin && (
        <a
          href={`mailto:${supportEmail}`}
          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "var(--pgm-amber)", textDecoration: "none", marginBottom: 14 }}
          data-testid="sidebar-support-link"
        >
          <LifeBuoy size={13} /> Need help? Contact support
        </a>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        {show?.host_image_url && !isSuperAdmin ? (
          <img src={resolveAsset(show.host_image_url)} alt="host" style={{ width: 32, height: 32, objectFit: "cover", border: "1.5px solid var(--pgm-amber)" }} />
        ) : (
          <div style={{ width: 32, height: 32, background: "var(--pgm-amber)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {isSuperAdmin ? <Crown size={14} color="var(--pgm-wine)" /> : <Users size={14} color="var(--pgm-wine)" />}
          </div>
        )}
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: "var(--pgm-cream)" }} data-testid="sidebar-user-name">{user.name}</div>
          <div style={{ color: "var(--pgm-amber)", textTransform: "uppercase", letterSpacing: "0.12em", fontSize: 9 }}>
            {isSuperAdmin ? "Super Admin" : "Admin"}
          </div>
        </div>
      </div>
      <button onClick={onLogout} className="pgm-btn pgm-btn-ghost" style={{ width: "100%", borderColor: "var(--pgm-cream)", color: "var(--pgm-cream)" }} data-testid="logout-btn">
        <LogOut size={14} /> Logout
      </button>
    </div>
  );
}

function ImpersonationBanner({ user, onExit }) {
  return (
    <div
      data-testid="impersonation-banner"
      style={{
        position: "sticky", top: 0, zIndex: 50, margin: "0 -40px 24px",
        background: "var(--pgm-amber)", color: "var(--pgm-wine)",
        padding: "10px 40px", display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "2px solid var(--pgm-wine)", fontSize: 13,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Eye size={15} />
        <span>Viewing as <strong>{user.name}</strong> ({user.email}) — impersonation mode</span>
      </span>
      <button
        onClick={onExit}
        className="pgm-btn pgm-btn-ghost"
        style={{ padding: "5px 14px", fontSize: 11, borderColor: "var(--pgm-wine)", color: "var(--pgm-wine)" }}
        data-testid="exit-impersonation-btn"
      >
        Exit to Super Admin
      </button>
    </div>
  );
}

export default function Layout() {
  const { user, logout, stopImpersonating } = useAuth();
  const [show, setShow] = useState(null);
  const [supportEmail, setSupportEmail] = useState("");
  const [navOpen, setNavOpen] = useState(false);
  const navigate = useNavigate();
  const isSuperAdmin = user?.role === "super_admin";
  const isImpersonating = !!user?.impersonating;

  useEffect(() => {
    api.get("/config").then(r => setSupportEmail(r.data.support_email)).catch((e) => console.warn("Config load failed:", e));
  }, []);

  // Close mobile drawer on any route change so nav clicks feel snappy
  useEffect(() => {
    const close = () => setNavOpen(false);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, []);

  const exitImpersonation = async () => {
    try {
      await stopImpersonating();
      navigate("/admin/platform");
    } catch (e) {
      console.warn("Exit impersonation failed", e);
    }
  };

  const loadShow = useCallback(async () => {
    if (isSuperAdmin) return; // platform owner has no show
    try {
      const { data } = await api.get("/shows/me");
      setShow(data);
    } catch (e) {
      if (e?.response?.status !== 404) console.warn("Show load failed:", e);
    }
  }, [isSuperAdmin]);

  useEffect(() => { loadShow(); }, [loadShow]);

  if (!user) { navigate("/login"); return null; }

  const mobileTitle = isSuperAdmin ? "ShowRunner HQ" : (show?.show_name || "ShowRunner HQ");

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--pgm-cream)" }}>
      <aside
        className={`pgm-sidebar ${navOpen ? "pgm-sidebar-open" : ""}`}
        data-testid="sidebar"
      >
        <SidebarBrand isSuperAdmin={isSuperAdmin} show={show} />
        <div onClick={() => setNavOpen(false)}>
          <SidebarNav isSuperAdmin={isSuperAdmin} />
        </div>
        <SidebarUser user={user} show={show} isSuperAdmin={isSuperAdmin} onLogout={logout} supportEmail={supportEmail} />
      </aside>
      <div
        className={`pgm-sidebar-backdrop ${navOpen ? "pgm-sidebar-backdrop-open" : ""}`}
        onClick={() => setNavOpen(false)}
        data-testid="sidebar-backdrop"
      />

      <main className="pgm-main" style={{ flex: 1, padding: "0 40px 120px", overflow: "auto", minWidth: 0 }}>
        <div className="pgm-mobile-topbar">
          <button
            className="pgm-hamburger"
            aria-label={navOpen ? "Close menu" : "Open menu"}
            onClick={() => setNavOpen(v => !v)}
            data-testid="mobile-nav-toggle"
          >
            {navOpen ? <XIcon size={22} /> : <Menu size={22} />}
          </button>
          <div className="pgm-mobile-title" data-testid="mobile-title">{mobileTitle}</div>
        </div>
        {isImpersonating && <ImpersonationBanner user={user} onExit={exitImpersonation} />}
        <div style={{ paddingTop: isImpersonating ? 0 : 32 }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
